import { Workbox } from 'workbox-window';

import LogService from '@/services/LogService';

import NotificationService from './services/NotificationService';

// VAPID public key from your backend environment variable
const VAPID_PUBLIC_KEY = process.env.VUE_APP_VAPID_PUBLIC_KEY;
let VUE_APP_VERSION = process.env.VUE_APP_VERSION;
if (!VUE_APP_VERSION) {
    VUE_APP_VERSION = generateRandomFallbackVersion();
}

function generateRandomFallbackVersion() {
    return Math.random().toString(36).substring(2, 10);
}

// Register the service worker if supported.
if ('serviceWorker' in navigator) {
    console.log('Service Worker is supported');
    window.swVersion = VUE_APP_VERSION;

    const wb = new Workbox(`/service-worker.js?v=${window.swVersion}`);
    console.log('Service Worker version:', window.swVersion);

    // Listen for the "waiting" event, which indicates a new SW is ready.
    wb.addEventListener('waiting', () => {
        console.log('New service worker waiting. Automatically skipping waiting and updating...');
        wb.messageSkipWaiting();
        wb.addEventListener('controlling', () => {
            console.log('New service worker controlling. Reloading page...');
            setTimeout(() => {
                console.log('REALOADING PAGE');
                window.location.reload();
            }, 1000);
        });
    });

    wb.register()
        .then((registration) => {
            if (!registration) {
                throw new Error('Service Worker registration failed.');
            }
            console.log('Service Worker registered with scope:', registration.scope);
            (window as any).swRegistration = registration;
        })
        .catch((error) => {
            console.error('Service Worker registration failed:', error);
        });
}

/**
 * Converts a base64 string to a Uint8Array (needed for the push subscription)
 */
function urlBase64ToUint8Array(base64String: any) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Requests Notification permission from the user.
 */
async function requestNotificationPermission() {
    if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        return false;
    }
    if (Notification.permission === 'granted') {
        return true;
    }
    if (Notification.permission === 'denied') {
        console.warn('Notification permission has been denied');
        return false;
    }
    const permission = await Notification.requestPermission();
    return permission === 'granted';
}

/**
 * Subscribes the user to push notifications using the provided service worker registration.
 */
async function subscribeToPushNotifications(registration: any) {
    if (!('pushManager' in registration)) {
        console.warn('Push notifications are not supported on this platform.');
        return null;
    }
    let subscription;
    try {
        subscription = await registration.pushManager.getSubscription();
        if (subscription) {
            console.log('Already subscribed to push notifications.');
        } else {
            if (!VAPID_PUBLIC_KEY) {
                LogService.error('VAPID_PUBLIC_KEY is not set.');
                return null;
            }
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
            });
            LogService.info('New push subscription created.');
        }
        // Send the subscription to your backend
        const response = await NotificationService.pushSubscribe(subscription);
        if (!response) {
            LogService.error('Failed to register subscription with backend.');
            throw new Error('Failed to register subscription with backend.');
        }
        LogService.info('Push subscription registered with backend.');
        return subscription;
    } catch (error) {
        LogService.error(`Error subscribing to push notifications: ${error}`);
        if (subscription) {
            try {
                const unsubscribed = await subscription.unsubscribe();
                LogService.info('Unsubscribed from push notifications due to error.');
                console.log('Unsubscribed from push notifications due to error.', unsubscribed);
            } catch (unsubscribeError) {
                LogService.error(`Error during unsubscription: ${unsubscribeError}`);
            }
        }
    }
}

export async function triggerPushSubscription() {
    // If swRegistration is not yet set, wait for the service worker to be ready.
    let registration = (window as any).swRegistration;
    if (!registration) {
        registration = await navigator.serviceWorker.ready;
        (window as any).swRegistration = registration; // Cache for future use.
    }

    if (!registration) {
        await LogService.error(`No service worker registration available.`);
        return;
    }

    await LogService.info(`LogService registration is valid`);

    let granted = null;
    const localSub = await registration.pushManager.getSubscription();
    const hasSub = await NotificationService.getHasPushSubscription();

    if (!localSub || !hasSub) {
        granted = await requestNotificationPermission();
        if (granted) {
            await subscribeToPushNotifications(registration);
            await LogService.info(`subscribeToPushNotifications completed`);
        } else {
            await LogService.error(`Permission not granted: ${granted}`);
        }
    } else {
        await LogService.info(`Push subscription already exists, skipping subscription.`);
    }
}

export async function checkForUpdatesAndReload() {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
        console.log('LogService No service worker registration found.');
        return;
    }

    // Force the browser to check for an updated service worker.
    await registration.update();

    // Now check if a new SW is waiting.
    if (registration.waiting) {
        // Send a message to the waiting SW to skip waiting.
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        // Listen for the new SW to take control.
        registration.waiting.addEventListener('statechange', (e: Event) => {
            if ((e.target as ServiceWorker).state === 'activated') {
                console.log('New service worker activated. Reloading page...');
                window.location.reload();
            }
        });
    } else {
        console.log('LogService No waiting service worker found after update check.');
    }
}
